import { useTranslationPlus } from "@/Core/i18n/useTranslationPlus";
import React, { FC, useMemo } from "react";
import classNames from "classnames";
import "./index.less";
import omit from "@/Core/utils/omit";
import ButtonUI from "@/componentsUI/Button";
import {
  CheckCircleFillIcon,
  ExclamationCircleIcon,
  QuestionCircleIcon,
} from "@/componentsUI/Icon";

type ResultStatus = "success" | "error" | "info" | "warning" | "404" | "403" | "500" | null;

export interface ResultUIProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  errorCode?: string;
  retryFunc?: () => void;
  btnText?: string;
  cusFunc?: () => void;
  errorText?: string;
  status?: ResultStatus;
  title?: React.ReactNode;
  subTitle?: React.ReactNode;
  icon?: React.ReactNode;
  extra?: React.ReactNode;
}

const ResultUI: FC<ResultUIProps> = (props) => {
  const { t: t2 } = useTranslationPlus("HomeView");
  const { t } = useTranslationPlus();
  const {
    btnText,
    className,
    cusFunc,
    errorCode,
    errorText,
    extra,
    icon,
    retryFunc,
    status,
    subTitle,
    title,
  } = props;
  const other = useMemo(() => {
    return omit(props, [
      "errorCode",
      "retryFunc",
      "btnText",
      "cusFunc",
      "errorText",
      "extra",
      "icon",
      "status",
      "subTitle",
      "title",
    ]);
  }, [props]);

  // 处理错误显示文本
  const getErrorTitle = () => {
    if (errorText) {
      return errorText;
    }
    if (errorCode) {
      return t(`${errorCode}`);
    }
    // 默认错误信息
    return t("ERROR_999") || "Network Error";
  };

  const renderIcon = () => {
    if (icon === null) {
      return null;
    }
    if (icon) {
      return icon;
    }
    if (status === "success") {
      return <CheckCircleFillIcon />;
    }
    if (status === "info") {
      return <QuestionCircleIcon />;
    }
    return <ExclamationCircleIcon />;
  };

  const resultIcon = renderIcon();
  const resultTitle = title || getErrorTitle();
  const resultExtra = (
    <>
      {extra}
      {btnText && (
        <ButtonUI
          key="again"
          onClick={() => {
            cusFunc ? cusFunc?.() : retryFunc?.();
          }}
        >
          {" "}
          {btnText}
        </ButtonUI>
      )}
      {!btnText && retryFunc && (
        <ButtonUI
          key="again"
          onClick={() => {
            retryFunc();
          }}
        >
          {" "}
          {btnText ? btnText : t2("Again")}
        </ButtonUI>
      )}
    </>
  );
  const showExtra = extra || btnText || retryFunc;

  return (
    <div
      {...other}
      className={classNames("pt-web-result", className)}
      data-status={status || undefined}
    >
      {resultIcon && <div className="pt-web-result-icon">{resultIcon}</div>}
      <div className="pt-web-result-title">{resultTitle}</div>
      {subTitle && <div className="pt-web-result-subtitle">{subTitle}</div>}
      {showExtra && <div className="pt-web-result-extra">{resultExtra}</div>}
    </div>
  );
};

export default React.memo(ResultUI);
